/**
 * @file useBuilderColumnWidths.ts
 * @description Ширины боковых колонок конструктора, которые сотрудник тянет
 * мышкой или пальцем, и свёртка колонок в полоску (phase19.16, партия после
 * приёмки Р2а).
 *
 * Один источник ширин для 1280 и 1600: сохранённая ширина важнее порога,
 * несохранённая берёт умолчание порога (`lib/site-builder/column-widths.ts`).
 * Место под колонки меряется по контейнеру сетки (`ResizeObserver`, без него —
 * по окну): не хватает места под канву — колонки ужимаются, память не трогается.
 *
 * Во время тяги ширина пишется прямо в CSS-переменную контейнера через
 * `requestAnimationFrame` — без перерисовки панелей и канвы на каждое
 * движение; в состояние и память — по отпусканию (`commit`). Не хватает
 * места под канву — соседняя колонка ужимается до своего минимума вместе
 * с тягой и тоже запоминается.
 *
 * Ревью партии SB122: свёртка, разворот и сброс отменяют ожидающий кадр
 * тяги (он рисовал бы по старым колонкам), а после каждой смены колонок
 * переменные контейнера сверяются с состоянием — ширина соседки, ужатой
 * во время тяги, не остаётся прежней, если React её не перезаписал.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import { useMediaQuery } from "../useMediaQuery";
import {
  clampColumn,
  collapsedFlag,
  gridTemplate,
  neighborFor,
  readStoredColumns,
  resolveColumns,
  writeStoredColumns,
  type BuilderColumnKey,
  type BuilderColumnsMode,
  type ResolvedColumns,
  type StoredColumns,
} from "../../lib/site-builder/column-widths";

/** Имя CSS-переменной ширины колонки на контейнере сетки. */
export function columnVar(key: BuilderColumnKey): `--builder-${BuilderColumnKey}` {
  return `--builder-${key}`;
}

const XL_QUERY = "(min-width: 1600px)";
const LG_QUERY = "(min-width: 1024px)";

/** Ширина контейнера; 0 у jsdom и до раскладки — «не измерено». */
function useContainerWidth() {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  useEffect(() => {
    if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      setWidth(w > 0 ? Math.round(w) : null);
    };
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [el]);
  return { el, ref: setEl, width };
}

export interface BuilderColumnWidths {
  /** Привязать к контейнеру сетки (мерить место и писать CSS-переменные). */
  containerRef: (el: HTMLElement | null) => void;
  /** Шаблон сетки и CSS-переменные ширин для `style` контейнера. */
  style: CSSProperties;
  /** Колонки раскладки: ширина, границы ручки, свёрнута ли. */
  columns: ResolvedColumns;
  /** Ширина во время тяги — только CSS-переменная, без перерисовки. */
  live: (key: BuilderColumnKey, px: number) => void;
  /** Отпустили ручку или нажали клавишу — в состояние и память. */
  commit: (key: BuilderColumnKey, px: number) => void;
  /** Двойной клик или Enter — снова умолчание порога. */
  reset: (key: BuilderColumnKey) => void;
  /** Свернуть колонку в полоску (ширина помнится). */
  collapse: (key: BuilderColumnKey) => void;
  /** Развернуть; `width` — с какой шириной (тянули от полоски — минимум). */
  expand: (key: BuilderColumnKey, width?: number) => void;
}

export function useBuilderColumnWidths(mode: BuilderColumnsMode, hasPanel: boolean): BuilderColumnWidths {
  const [stored, setStored] = useState<StoredColumns>(readStoredColumns);
  const xl = useMediaQuery(XL_QUERY);
  const lg = useMediaQuery(LG_QUERY);
  const container = useContainerWidth();
  const frame = useRef<number | null>(null);

  const columns = useMemo(
    () => resolveColumns({ mode, hasPanel, stored, thresholds: { xl, lg }, containerWidth: container.width }),
    [mode, hasPanel, stored, xl, lg, container.width],
  );

  const style = useMemo(() => {
    const vars: Record<string, string> = { gridTemplateColumns: gridTemplate(columns) };
    for (const [key, col] of Object.entries(columns)) {
      // Свёрнутой колонке переменная не нужна: в сетке у неё постоянные 48 px.
      if (!col.collapsed) vars[columnVar(key as BuilderColumnKey)] = `${col.width}px`;
    }
    return vars as CSSProperties;
  }, [columns]);

  const { el, width: containerWidth } = container;
  /** Ширины колонки и соседки (ужатой или своей) — прямо в CSS-переменные. */
  const paint = useCallback(
    (key: BuilderColumnKey, px: number) => {
      if (!el) return;
      el.style.setProperty(columnVar(key), `${px}px`);
      const near = neighborFor(columns, key, px, containerWidth);
      const otherKey: BuilderColumnKey | null = key === "nav" ? "panel" : key === "panel" ? "nav" : null;
      const other = otherKey ? columns[otherKey] : undefined;
      if (otherKey && other && !other.collapsed) el.style.setProperty(columnVar(otherKey), `${near?.width ?? other.width}px`);
    },
    [el, columns, containerWidth],
  );
  const live = useCallback(
    (key: BuilderColumnKey, px: number) => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        paint(key, px);
      });
    },
    [paint],
  );
  /** Отменить ожидающий кадр тяги. */
  const cancelFrame = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
  }, []);
  useEffect(() => cancelFrame, [cancelFrame]);

  // Колонки сменились (свёртка, разворот, память, место) — переменные контейнера
  // = состоянию: то, что тяга нарисовала мимо React, не переживает смену.
  useLayoutEffect(() => {
    if (!el) return;
    cancelFrame();
    for (const [key, col] of Object.entries(columns)) {
      const name = columnVar(key as BuilderColumnKey);
      if (col.collapsed) el.style.removeProperty(name);
      else el.style.setProperty(name, `${col.width}px`);
    }
  }, [el, columns, cancelFrame]);

  const save = useCallback((update: (prev: StoredColumns) => StoredColumns) => {
    setStored((prev) => {
      const next = update(prev);
      writeStoredColumns(next);
      return next;
    });
  }, []);

  const commit = useCallback(
    (key: BuilderColumnKey, px: number) => {
      // Последний кадр тяги не должен перебить итоговую ширину.
      cancelFrame();
      const width = clampColumn(key, px);
      paint(key, width);
      const near = neighborFor(columns, key, width, containerWidth);
      save((prev) => ({ ...prev, [key]: width, ...(near ? { [near.key]: near.width } : {}) }));
    },
    [paint, columns, containerWidth, save, cancelFrame],
  );

  // Свёртка, разворот и сброс: кадр тяги со старыми колонками больше не нужен.
  const reset = useCallback(
    (key: BuilderColumnKey) => {
      cancelFrame();
      save((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [save, cancelFrame],
  );

  const collapse = useCallback(
    (key: BuilderColumnKey) => {
      cancelFrame();
      save((prev) => ({ ...prev, [collapsedFlag(key)]: true }));
    },
    [save, cancelFrame],
  );

  const expand = useCallback(
    (key: BuilderColumnKey, width?: number) => {
      cancelFrame();
      save((prev) => {
        const next: StoredColumns = { ...prev };
        delete next[collapsedFlag(key)];
        if (width !== undefined) next[key] = clampColumn(key, width);
        return next;
      });
    },
    [save, cancelFrame],
  );

  return { containerRef: container.ref, style, columns, live, commit, reset, collapse, expand };
}
